'use client'

import * as React from 'react'
import { cn } from '../../../lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverAnchor,
} from '../Popover'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../Drawer'
import { useMediaQuery } from '../Threads/hooks/useMediaQuery'

interface ResponsivePopoverProps {
  children: React.ReactNode
  trigger?: React.ReactNode
  /** Anchor element for controlled popovers opened from another control (e.g. dropdown menu). */
  anchor?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  title?: string
  /** className applied to both PopoverContent and DrawerContent */
  className?: string
  /** className applied only to PopoverContent (desktop) */
  popoverClassName?: string
  /** className applied only to DrawerContent (mobile) */
  drawerClassName?: string
  /** Popover alignment on desktop */
  popoverAlign?: 'start' | 'center' | 'end'
  popoverSide?: 'top' | 'right' | 'bottom' | 'left'
  popoverSideOffset?: number
}

export function ResponsivePopover({
  children,
  trigger,
  anchor,
  open,
  onOpenChange,
  title,
  className,
  popoverClassName,
  drawerClassName,
  popoverAlign = 'center',
  popoverSide,
  popoverSideOffset,
}: ResponsivePopoverProps) {
  const isMobile = useMediaQuery('(max-width: 767px)')
  const resolvedTrigger = anchor ?? trigger

  if (!resolvedTrigger) {
    return null
  }

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerTrigger asChild>{resolvedTrigger}</DrawerTrigger>
        <DrawerContent
          className={cn(
            'max-h-[90vh] flex flex-col',
            drawerClassName ?? className,
          )}
        >
          {title && (
            <DrawerHeader className="text-left pb-2">
              <DrawerTitle>{title}</DrawerTitle>
            </DrawerHeader>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {anchor ? (
        <PopoverAnchor asChild>{anchor}</PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      <PopoverContent
        align={popoverAlign}
        side={popoverSide}
        sideOffset={popoverSideOffset}
        className={popoverClassName ?? className}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}
